import React, { useEffect, useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { Tabs, Tab } from '@mui/material';

import Logo from '../assets/images/datalive_logo_color_large.png';
import IconMailWihte from '../assets/images/common/icon-mail-wihte.svg';
import IconLibraryOrange from '../assets/images/common/icon-library-orange.svg';

import { env_user } from '../conf/environ_user';

import { STARTUP_API_ERRORS } from '../types/api/StartApiType';
import { MASTER_PREFECTURE_API_ERRORS } from '../types/api/MasterPrefectureApiType';
import { MASTER_INQUIRE_API_ERRORS } from '../types/api/MasterInquireApiType';
import { MASTER_SLA_API_ERRORS } from '../types/api/MasterSlaApiType';
import { ErrorMessages } from '../types/common/Messages';
import { TAB_ITEM_LIST, TEMP_SAVE_PATH_LIST } from '../types/common/CommonType';

import { initializeApp } from '../api/init';
import { masterApi } from '../api/master';
import { ErrorModalModal } from '../components/ErrorModal';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { tabNavigation } from '../utils/tabChangeUtil';

type PageHeaderProps = {
    setOpenTutorialModal: (value: boolean) => void; // チュートリアル開閉
    tabIndex: number;
    setTabIndex: (value: number) => void;
};

export const PageHeader: React.FC<PageHeaderProps> = ({ setOpenTutorialModal, tabIndex, setTabIndex }) => {
    // マスタ取得処理エラー管理
    const [errorModalMessages, setErrorModalMessages] = useState<Array<string>>([]);
    const [openErrorlModal, setOpenErrorModal] = useState<boolean>(false);

    // 現在のpath取得
    const location = useLocation();
    const navigation = useNavigate();

    // マスタ取得有無判定用
    const dispatch = useAppDispatch();
    const clientId = useAppSelector((s) => s.ClientId);
    const masterPrefecture = useAppSelector((s) => s.MasterPrefecture);
    const masterInquire = useAppSelector((s) => s.MasterInquire);
    const masterSla = useAppSelector((s) => s.MasterSla);
    const searchResltLength = useAppSelector((s) => s.SearchResult).count;

    useEffect(() => {
        // 各マスタ取得処理
        const getMasterData = async () => {
            let apiErrorResult = '';
            // clientID設定処理
            const initApiResult = await initializeApp(dispatch, clientId);
            if (initApiResult.includes(STARTUP_API_ERRORS.REQUEST_ERROR_8001)) {
                // initApiResultが異常終了した場合
                apiErrorResult = initApiResult;
            }
            // 都道府県マスタ取得
            if (apiErrorResult === '' && (masterPrefecture?.prefecture ?? []).length === 0) {
                const getMasterPrefectureResult = await masterApi.getMasterPrefecture(dispatch);
                if (getMasterPrefectureResult.includes(MASTER_PREFECTURE_API_ERRORS.REQUEST_ERROR_9001)) {
                    apiErrorResult = getMasterPrefectureResult;
                }
            }
            // お問い合わせマスタ取得
            if (apiErrorResult === '' && (masterInquire?.inquire_type ?? []).length === 0) {
                const getMasterInquireResult = await masterApi.getMasterInquire(dispatch);
                if (getMasterInquireResult.includes(MASTER_INQUIRE_API_ERRORS.REQUEST_ERROR_9002)) {
                    apiErrorResult = getMasterInquireResult;
                }
            }
            // 保守レベルのマスタ取得
            if (apiErrorResult === '' && (masterSla ?? []).length === 0) {
                const getMasterSlaResult = await masterApi.getMasterSla(dispatch);
                if (getMasterSlaResult.includes(MASTER_SLA_API_ERRORS.REQUEST_ERROR_9003)) {
                    apiErrorResult = getMasterSlaResult;
                }
            }

            // エラー時制御
            if (apiErrorResult !== '') {
                setErrorModalMessages([ErrorMessages.API900x_INIT_ERROR.replace('%1', apiErrorResult)]);
                setOpenErrorModal(true);
            }
        };
        void getMasterData();

        // 復帰時のタブを設定する
        if (location.pathname) {
            const tempSaveTabIndex = TEMP_SAVE_PATH_LIST.find((it) =>
                location.pathname?.startsWith('/' + it.path)
            )?.tabIndex;
            if (tempSaveTabIndex) {
                setTabIndex(tempSaveTabIndex);
            }
        }
    }, [location]);

    // 画面遷移時にスクロール位置をトップに移動させる
    useEffect(() => {
        window.scrollTo(0, 0);
        // 現在のパスによって、タブの選択状態を最新
        TAB_ITEM_LIST.forEach((tabItem) => {
            if (tabItem.pathnameList.includes(location.pathname) && tabIndex !== tabItem.tabIndex) {
                // タブクリック以外の方法で画面遷移の時、タブ状態を合わせる
                setTabIndex(tabItem.tabIndex);
            }
        });
    }, [location]);

    // レスポンシブ表示編集
    const navberClick = () => {
        const navbarBurgerClass = document.getElementsByClassName('navbar-burger')[0];
        const navbarMenuClass = document.getElementsByClassName('navbar-menu')[0];
        navbarBurgerClass.classList.toggle('is-active');
        navbarMenuClass.classList.toggle('is-active');
    };

    // タブの画面遷移制御
    const tabMove = (event: React.SyntheticEvent, clickIndex: number) => {
        void tabNavigation(clickIndex, searchResltLength, navigation, setTabIndex);
    };

    return (
        <>
            <header id='header'>
                <span className='head_nab_gra'></span>
                <nav className='navbar' role='navigation' aria-label='main navigation'>
                    <div className='container'>
                        <div className='navbar-brand'>
                            <Link to={env_user.header_logo_link_url} className='navbar-item logo'>
                                <img src={Logo} width='153' height='35.32' />
                            </Link>
                            <a
                                role='button'
                                className='navbar-burger'
                                aria-label='menu'
                                aria-expanded='false'
                                data-target='navMenu_sp'
                                onClick={() => navberClick()}
                            >
                                <span aria-hidden='true'></span>
                                <span aria-hidden='true'></span>
                                <span aria-hidden='true'></span>
                            </a>
                        </div>
                        <div id='navMenu_sp' className='navbar-menu is-active'>
                            <div className='navbar-start'>
                                <Tabs value={tabIndex} TabIndicatorProps={{ style: { backgroundColor: 'white' } }}>
                                    {TAB_ITEM_LIST.map((tabItem) => {
                                        return (
                                            <Tab
                                                key={`pageHeader-tab-${tabItem.tabIndex}`}
                                                className='header-tab'
                                                label={<span className='tab-title'>{tabItem.tableName}</span>}
                                                onClick={(e) => tabMove(e, tabItem.tabIndex)}
                                            />
                                        );
                                    })}
                                </Tabs>
                                <Link to='/contact/header' className='navbar-item only-sp'>
                                    保守に関するお問い合わせ
                                </Link>
                            </div>
                        </div>
                        <div className='navbar-end'>
                            <div className='navbar-item'>
                                <div className='buttons'>
                                    <Link to='/contact/header' className='button is-primary'>
                                        <span>保守に関するお問い合わせ</span>
                                        <span className='icon'>
                                            <img src={IconMailWihte} alt='' />
                                        </span>
                                    </Link>
                                    <div className='nav-tutorial' onClick={() => setOpenTutorialModal(true)}>
                                        <a className='js-modal-trigger' data-target='modal-first'>
                                            <img src={IconLibraryOrange} alt='' />
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </nav>
            </header>
            <ErrorModalModal
                errorMessages={errorModalMessages}
                openState={openErrorlModal}
                setOpenState={setOpenErrorModal}
            />
        </>
    );
};
